import badgeUnlockRules from '../../consts/loyalty/badgeUnlockRules';
import {selectIsAnyDeviceRegistered} from '../../state/selectors/iotDevices';
import {selectBadgeData, selectCompletedBadges} from '../../state/selectors/loyalty';
import {getState} from '../../state/store';
import {getIsSwitchMasterBadgeAvailable} from './loyaltyHelpers';

const DAY = 86_400_000;

const d = (ms) => ms / DAY;

export const toMs = (ts) => {
    if (!ts && ts !== 0) return null;
    return ts > 1e12 ? ts : ts * 1000;
};

const applyLevelRule = (rule) => {
    switch (rule) {
        case badgeUnlockRules.DELAY_3_DAYS_AFTER_FIRST_COMPLETION:
            const completedBadges = selectCompletedBadges(getState()) || [];

            if (!completedBadges.length) {
                return {isAvailable: false, daysLeft: null};
            }

            const completedTimestamps = completedBadges?.map((b) => b.levelCompletedTs).filter(Boolean);
            const firstCompletedBadgeTs = Math.min(...completedTimestamps);

            if (firstCompletedBadgeTs) {
                const daysSinceFirstCompletion = d(Date.now() - firstCompletedBadgeTs);
                const daysLeft = 3 - daysSinceFirstCompletion;

                return {
                    isAvailable: daysLeft <= 0,
                    daysLeft,
                };
            }
            return {};

        case badgeUnlockRules.DEVICE_REQUIRED:
            const isAnyDeviceRegistered = selectIsAnyDeviceRegistered(getState());

            return {isAvailable: isAnyDeviceRegistered};

        case badgeUnlockRules.CONVERSIONABLE: {
            const isAvailable = getIsSwitchMasterBadgeAvailable();

            return {isAvailable};
        }

        default: {
            return {isAvailable: true};
        }
    }
};

const checkBadgeUnlockRules = (badgeType) => {
    if (!badgeType) {
        return {isLocked: false};
    }

    const badgeData = selectBadgeData(badgeType)(getState());
    const {index, parentBadgeType, rules} = badgeData;

    // If no rules exist - badge is unlocked
    if (!rules?.length) {
        return {isLocked: false};
    }

    // If the sub badge is the first one - badge is unlocked (only for SWITCH_MASTER_LEVEL_1)
    if (index === 1) {
        return {isLocked: false};
    }

    // Get previous badge data (index - 2 because array starts from 0)
    const parentBadgeData = selectBadgeData(parentBadgeType)(getState());
    const prevBadgeType = parentBadgeData?.levels?.[index - 2];

    // Process rules in priority order
    for (const rule of rules) {
        const {isAvailable, daysLeft} = applyLevelRule(rule);

        // Stop at first rule that locks the badge
        if (!isAvailable) {
            return {
                isLocked: true,
                lockingRule: rule,
                daysLeft,
                prevBadgeType,
            };
        }
    }

    // Badge is unlocked by default
    return {isLocked: false};
};

export const calcDaysLeft = (futureTs) => {
    const ms = toMs(futureTs);

    if (!ms) return 0;

    const diff = ms - Date.now();

    if (diff <= 0) return 0;

    const days = diff / DAY;

    if (days < 1) {
        return Number(days.toFixed(5));
    }

    return Math.ceil(days);
};

export default {
    checkBadgeUnlockRules,
    applyLevelRule,
};
